import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

/**
 * Servicio del rail R4 Conecta (Mi Banco). Espejo de n58.service / banco-plaza.service.
 * Por ahora cubre el toggle PAGO_R4 (paso 3 del plan de front). El dashboard R4
 * (operations/stats/monto) se agregará en el paso 4.
 */
export interface R4Operation {
  id: string;
  operationType: string;
  status: string;
  referencia: string;
  externalId: string | null;
  codigoRespuesta: string | null;
  codigoIso: string | null;
  mensajeRespuesta: string | null;
  monto: number | null;
  cedula: string | null;
  telefono: string | null;
  codigoBanco: string | null;
  cuenta: string | null;
  operador: string | null;
  transaccionId: number | null;
  walletCardexVesId: string | null;
  pollCount: number;
  createdAt: string;
  completedAt: string | null;
  rawRequest?: any;
  rawResponse?: any;
}

export interface R4Stats {
  success: boolean;
  total: number;
  completed: number;
  pending: number;
  rejected: number;
  error: number;
  timeout: number;
  byStatus: Record<string, number>;
}

export interface R4Monto {
  success: boolean;
  montoMin: number;
  montoMax: number;
}

export interface R4Health {
  operational: boolean;
  tasaBcv: number | null;
  error: string | null;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class R4Service {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio R4';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  /** Estado del toggle PAGO_R4 (encendido/apagado del rail). */
  getPagoR4Status(): Observable<{ success: boolean; active: boolean }> {
    const url = `${this.baseUrl}r4-conecta/pago-r4/status`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<{ success: boolean; active: boolean }>(url, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  /** Alterna PAGO_R4 y devuelve el nuevo estado. */
  togglePagoR4(): Observable<{ success: boolean; active: boolean }> {
    const url = `${this.baseUrl}r4-conecta/pago-r4/toggle`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<{ success: boolean; active: boolean }>(url, {}, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  /** Lista de operaciones R4 (r4_operations), filtros opcionales. */
  getOperations(params: { status?: string; type?: string; limit?: number } = {}): Observable<R4Operation[]> {
    let qs = `?limit=${params.limit ?? 50}`;
    if (params.status) qs += `&status=${encodeURIComponent(params.status)}`;
    if (params.type) qs += `&type=${encodeURIComponent(params.type)}`;
    const url = `${this.baseUrl}r4-conecta/operations${qs}`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<R4Operation[]>(url, { headers })),
      catchError(this.handleError),
    );
  }

  /** Fuerza el polling de estado de una operación contra el banco. */
  pollOperation(id: string): Observable<R4Operation> {
    const url = `${this.baseUrl}r4-conecta/operations/${id}/poll`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<R4Operation>(url, {}, { headers })),
      catchError(this.handleError),
    );
  }

  /** Health: ping de conectividad/credenciales vía MBbcv. */
  getHealth(): Observable<R4Health> {
    const url = `${this.baseUrl}r4-conecta/health`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<R4Health>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getStats(): Observable<R4Stats> {
    const url = `${this.baseUrl}r4-conecta/stats`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<R4Stats>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getMonto(): Observable<R4Monto> {
    const url = `${this.baseUrl}r4-conecta/monto`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<R4Monto>(url, { headers })),
      catchError(this.handleError),
    );
  }

  setMonto(montoMin: number, montoMax: number): Observable<R4Monto> {
    const url = `${this.baseUrl}r4-conecta/monto`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<R4Monto>(url, { montoMin, montoMax }, { headers })),
      catchError(this.handleError),
    );
  }
}
